// Shared UI helpers, formatters and constants for the AI Outbound panels.
// Extracted from AIOutbound.jsx during the P1 refactor so panel files
// (TemplatesPanel, AnalyticsPanel, …) can stay small and self-contained.

import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";

// ---------------------------------------------------------------- formatters
export const fmtDate = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
export const fmtUsd = (v) => `$${Number(v || 0).toFixed(2)}`;

// ---------------------------------------------------------------- constants
export const TONES = [
  { value: "conversational", label: "Conversational" },
  { value: "formal", label: "Formal" },
  { value: "energetic", label: "Energetic" },
  { value: "empathetic", label: "Empathetic" },
];

export const OUTCOME_TONE = {
  booked: "bg-emerald-50 text-emerald-700 border-emerald-200",
  interested: "bg-sky-50 text-sky-700 border-sky-200",
  talk_declined: "bg-slate-100 text-slate-600 border-slate-200",
  voicemail: "bg-amber-50 text-amber-700 border-amber-200",
  no_answer: "bg-slate-100 text-slate-500 border-slate-200",
  wrong_number: "bg-rose-50 text-rose-700 border-rose-200",
  do_not_call: "bg-red-50 text-red-700 border-red-200",
  capped: "bg-purple-50 text-purple-700 border-purple-200",
};

export const LIFECYCLE_TABS = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "deleted", label: "Deleted" },
  { value: "all", label: "All" },
];

export const TEMPLATE_VISIBILITY_TABS = [
  { value: "all", label: "All" },
  { value: "staff", label: "Staff" },
  { value: "staff_partners", label: "Staff + Partners" },
  { value: "staff_partners_customers", label: "Everyone" },
];

export const VISIBILITY_LABELS = {
  staff: { text: "Staff", cls: "border-slate-200 bg-slate-50 text-slate-700" },
  staff_partners: { text: "Staff + Partners", cls: "border-amber-200 bg-amber-50 text-amber-800" },
  staff_partners_customers: { text: "Everyone", cls: "border-emerald-200 bg-emerald-50 text-emerald-800" },
};

export const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const EMPTY_SCRIPT = () => ({
  opening: "", value_prop: "", qualifying_question: "",
  value_bullets: [], call_to_action: "",
  objections: [], close: "",
});

export const SCRIPT_VARIABLES = [
  { token: "{agent_name}", desc: "The AI agent's spoken name (from campaign)" },
  { token: "{company_name}", desc: "Recipient's company (from list)" },
  { token: "{recipient_name}", desc: "Recipient's first name (from list)" },
  { token: "{day_time}", desc: "Next available slot from Calendly" },
];

// ---------------------------------------------------------------- layout primitives
export function Row2({ children }) {
  return <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>;
}

export function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

/** Renders a Field-compatible label with an inline `?` icon that opens a tooltip
 *  on hover / focus. Use as the `label` prop of <Field>. */
export function LabelWithHint({ text, hint, testId }) {
  return (
    <TooltipProvider delayDuration={200}>
      <span className="inline-flex items-center gap-1.5">
        {text}
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="text-slate-400 hover:text-slate-600"
                    data-testid={testId} aria-label={`What is ${text}?`}>
              <HelpCircle className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs" data-testid={`${testId}-tooltip`}>
            {hint}
          </TooltipContent>
        </Tooltip>
      </span>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------- badges & cards
export function StatusBadge({ status }) {
  const tone = {
    draft: "bg-slate-100 text-slate-600 border-slate-200",
    running: "bg-sky-50 text-sky-700 border-sky-200",
    paused: "bg-amber-50 text-amber-700 border-amber-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  }[status] || "bg-slate-100 text-slate-500 border-slate-200";
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${tone}`}>{status || "—"}</span>;
}

export function MetricCard({ icon: Icon, label, value, tone = "slate" }) {
  const toneCls = {
    slate: "text-slate-700", emerald: "text-emerald-700", sky: "text-sky-700", amber: "text-amber-700",
  }[tone];
  return (
    <Card className="border border-slate-200 p-4" data-testid={`ai-outbound-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-400"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`mt-1 font-heading text-2xl font-semibold ${toneCls}`}>{value}</div>
    </Card>
  );
}

export function ConfigRow({ k, v }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-28 shrink-0 text-[11px] uppercase tracking-wide text-slate-400">{k}</dt>
      <dd className="text-slate-700">{v}</dd>
    </div>
  );
}
